/**
 * Legal policies for SB Media Studio (Lakeland, FL).
 * Data-driven so the same <LegalPage> renders all five.
 *
 * IMPORTANT: These are industry-standard policy templates tailored for a
 * small-business media studio. Have counsel review before making
 * jurisdiction-specific commitments (e.g. HIPAA, healthcare, minors' data).
 */

const COMPANY = "SB Media Studio";
const CITY = "Lakeland, Florida";
const CONTACT_EMAIL = "Info@sbmediastudio.com";
const WEBSITE = "https://www.sbmediastudio.com";
const GOVERNING_LAW = "State of Florida, USA";
const EFFECTIVE = "February 15, 2026";

export const POLICIES = {
  "privacy-policy": {
    title: "Privacy Policy",
    kicker: "How we collect, use, and protect your information",
    effective: EFFECTIVE,
    sections: [
      {
        heading: "1. Who we are",
        body: [
          `${COMPANY} ("we", "us", or "our") operates ${WEBSITE} and provides podcast production, studio rental, headshots, and UGC services from ${CITY}. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, book a service, or otherwise interact with us.`,
        ],
      },
      {
        heading: "2. Information we collect",
        body: [
          "**Information you provide directly** — name, email address, phone number, business name, project details, payment information (processed via third-party processors), messages you send via forms or the Sable AI chat widget, and any content you submit for guest applications.",
          "**Information collected automatically** — IP address, browser type and version, device identifiers, pages viewed, referring URL, and interactions with the site (via cookies and similar technologies — see our Cookie Policy).",
          "**Information from third parties** — booking analytics, email delivery data (via Resend), and workflow data from integrations you authorize (e.g., Monday.com).",
        ],
      },
      {
        heading: "3. How we use your information",
        body: [
          "• Deliver, operate, and maintain the services you request.",
          "• Communicate about bookings, invoices, project updates, and support.",
          "• Send marketing emails (only where you have opted in or where permitted by law) — you can unsubscribe from any marketing email at any time.",
          "• Improve our website, services, and internal operations.",
          "• Comply with legal obligations, resolve disputes, and enforce our agreements.",
        ],
      },
      {
        heading: "4. Legal bases for processing (GDPR / EEA visitors)",
        body: [
          "Where GDPR applies, we rely on: (a) your **consent** for optional marketing and non-essential cookies; (b) **contract** performance for delivering services you've booked; (c) our **legitimate interests** for site security, fraud prevention, and improving our services; and (d) **legal obligations**.",
        ],
      },
      {
        heading: "5. How we share information",
        body: [
          `We do **not** sell your personal information. We share it only with: (a) trusted service providers who process data on our behalf under confidentiality obligations (payment processors, email delivery, hosting, analytics, workflow tools like Monday.com); (b) professional advisors (accountants, lawyers); and (c) authorities when required by law or to protect rights and safety.`,
        ],
      },
      {
        heading: "6. Your rights",
        body: [
          "Depending on your jurisdiction, you may have rights to access, correct, delete, port, or object to processing of your personal information, and to withdraw consent. California residents have additional rights described in our *Do Not Sell or Share My Personal Information* notice. To exercise any of these rights, email us at " +
            CONTACT_EMAIL +
            ".",
        ],
      },
      {
        heading: "7. Data retention",
        body: [
          "We keep information only as long as necessary to fulfill the purposes described here, comply with legal, accounting, or reporting requirements, and resolve disputes. Booking and invoice records are typically retained for seven (7) years for tax compliance.",
        ],
      },
      {
        heading: "8. Data security",
        body: [
          "We use industry-standard technical and organizational measures (encryption in transit via TLS, access controls, hashed credentials, audit logging) to protect personal information. No method of transmission or storage is 100% secure, and we cannot guarantee absolute security.",
        ],
      },
      {
        heading: "9. Children's privacy",
        body: [
          "Our services are not directed to children under 13, and we do not knowingly collect personal information from children under 13. If you believe a child has provided us information, contact us and we will delete it promptly.",
        ],
      },
      {
        heading: "10. International transfers",
        body: [
          "We are based in the United States. If you are visiting from outside the US, your information will be transferred to and processed in the US. By using our site you consent to this transfer.",
        ],
      },
      {
        heading: "11. Changes to this policy",
        body: [
          "We may update this Privacy Policy from time to time. The updated version will be indicated by a revised \"Effective\" date and will be effective as soon as it is accessible. Material changes will be posted prominently.",
        ],
      },
      {
        heading: "12. Contact",
        body: [
          `${COMPANY} · ${CITY} · Email: ${CONTACT_EMAIL}`,
        ],
      },
    ],
  },

  "terms-of-use": {
    title: "Terms of Use",
    kicker: "The agreement between you and SB Media Studio",
    effective: EFFECTIVE,
    sections: [
      {
        heading: "1. Acceptance of terms",
        body: [
          `By accessing or using ${WEBSITE} or booking any service with ${COMPANY}, you agree to be bound by these Terms of Use ("Terms"). If you do not agree, do not use the site or services.`,
        ],
      },
      {
        heading: "2. Services",
        body: [
          "We offer podcast production, studio rental, headshots, UGC, and related media services. Specific deliverables, timelines, and payment terms are detailed in each booking confirmation or written statement of work. Prices and package inclusions are subject to change without notice; the price in your booking confirmation controls.",
        ],
      },
      {
        heading: "3. Bookings, payment, and cancellations",
        body: [
          "**Bookings** are confirmed upon receipt of payment (or deposit) and issuance of a confirmation email. **Rescheduling** requests made 72+ hours before the session are accommodated at no charge, subject to availability. **Cancellations** within 72 hours of a session may forfeit the deposit. **No-shows** forfeit the full booking amount. Refunds are handled on a case-by-case basis.",
        ],
      },
      {
        heading: "4. Content ownership & licensing",
        body: [
          "Unless otherwise stated in a written agreement: (a) **Raw footage and stems** remain the property of the client after full payment; (b) ${COMPANY} retains a non-exclusive, royalty-free license to use excerpts of delivered work for portfolio, marketing, and case-study purposes; (c) Third-party assets (music, stock footage) are licensed for your project only and may not be extracted for reuse.".replace('${COMPANY}', COMPANY),
        ],
      },
      {
        heading: "5. Client responsibilities",
        body: [
          "You represent and warrant that: (a) you have all necessary rights, releases, and permissions for any content, likenesses, brand assets, or third-party materials you provide; (b) your project complies with applicable laws; and (c) you will indemnify ${COMPANY} against any third-party claim arising from content you provide.".replace('${COMPANY}', COMPANY),
        ],
      },
      {
        heading: "6. Acceptable use",
        body: [
          "You agree not to: (a) use the services for unlawful, defamatory, hateful, harassing, or misleading purposes; (b) reverse-engineer, scrape, or interfere with the site; (c) upload malware or attempt to compromise security; or (d) impersonate any person or misrepresent your affiliation with anyone.",
        ],
      },
      {
        heading: "7. Third-party integrations",
        body: [
          "The site integrates with third-party services (payment processors, email delivery, AI chat, workflow tools). Your use of those services is governed by their own terms. We are not responsible for third-party service outages or acts.",
        ],
      },
      {
        heading: "8. Disclaimers",
        body: [
          `SERVICES AND THE SITE ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. ${COMPANY.toUpperCase()} DISCLAIMS ALL IMPLIED WARRANTIES INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT UNINTERRUPTED OR ERROR-FREE OPERATION.`,
        ],
      },
      {
        heading: "9. Limitation of liability",
        body: [
          `TO THE MAXIMUM EXTENT PERMITTED BY LAW, ${COMPANY.toUpperCase()}'S TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICES IS LIMITED TO THE AMOUNT YOU PAID FOR THE SERVICE GIVING RISE TO THE CLAIM IN THE TWELVE (12) MONTHS BEFORE THE EVENT. WE ARE NOT LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.`,
        ],
      },
      {
        heading: "10. Indemnification",
        body: [
          `You agree to defend, indemnify, and hold harmless ${COMPANY}, its owners, employees, and contractors from any claim, damage, or expense (including reasonable attorneys' fees) arising from your breach of these Terms or your use of the services.`,
        ],
      },
      {
        heading: "11. Governing law & venue",
        body: [
          `These Terms are governed by the laws of the ${GOVERNING_LAW}, without regard to conflict-of-law rules. Any dispute will be resolved exclusively in the state or federal courts located in Polk County, Florida, and you consent to personal jurisdiction there.`,
        ],
      },
      {
        heading: "12. Changes to these terms",
        body: [
          "We may revise these Terms at any time. Revisions take effect when posted. Continued use of the site or services after posting constitutes acceptance.",
        ],
      },
      {
        heading: "13. Contact",
        body: [`${COMPANY} · ${CITY} · Email: ${CONTACT_EMAIL}`],
      },
    ],
  },

  "do-not-sell": {
    title: "Do Not Sell or Share My Personal Information",
    kicker:
      "Your California privacy rights under CCPA / CPRA — and how to exercise them",
    effective: EFFECTIVE,
    sections: [
      {
        heading: "1. Our commitment",
        body: [
          `${COMPANY} does **not** sell your personal information for money. Under the California Consumer Privacy Act (CCPA) as amended by the California Privacy Rights Act (CPRA), certain sharing of personal information for cross-context behavioral advertising may be treated as a "sale" or "share." This page explains how to opt out.`,
        ],
      },
      {
        heading: "2. Your CCPA/CPRA rights",
        body: [
          "If you are a California resident, you have the right to: (a) **know** what personal information we collect, use, disclose, and share; (b) **delete** personal information we have collected from you; (c) **correct** inaccurate personal information; (d) **opt out** of the sale or sharing of your personal information; (e) **limit** the use of sensitive personal information; and (f) be free from retaliation for exercising these rights.",
        ],
      },
      {
        heading: "3. Categories of personal information we handle",
        body: [
          "• Identifiers (name, email, phone, IP address)",
          "• Commercial information (bookings, invoices, package selections)",
          "• Internet activity (pages viewed, referrer, chat transcripts)",
          "• Professional information (business name, project details)",
        ],
      },
      {
        heading: "4. How to opt out",
        body: [
          `To opt out of any sharing of your personal information for cross-context behavioral advertising, email us at ${CONTACT_EMAIL} with the subject line "**Do Not Sell or Share**" including the email address associated with your account or bookings. We will process your request within 15 business days.`,
          "You may also opt out by enabling the Global Privacy Control (GPC) signal in a supporting browser — we honor GPC where technically feasible.",
        ],
      },
      {
        heading: "5. Authorized agents",
        body: [
          "You may designate an authorized agent to submit a request on your behalf. We will require written authorization and verification of your identity before processing.",
        ],
      },
      {
        heading: "6. Verification",
        body: [
          "We will verify your identity before honoring any request by matching the information you provide against records we already hold. We may request additional information if necessary.",
        ],
      },
      {
        heading: "7. Contact",
        body: [
          `Questions about your California privacy rights? Email ${CONTACT_EMAIL} — subject line: "**CCPA Request**".`,
        ],
      },
    ],
  },

  "cookie-policy": {
    title: "Cookie Policy",
    kicker: "How and why we use cookies and similar technologies",
    effective: EFFECTIVE,
    sections: [
      {
        heading: "1. What are cookies?",
        body: [
          "Cookies are small text files stored on your device when you visit a website. They allow the site to remember your actions and preferences (login, language, session state) over a period of time so you don't have to re-enter them.",
        ],
      },
      {
        heading: "2. Types of cookies we use",
        body: [
          "**Strictly necessary** — Required for the site to function (session, authentication, security). These cannot be disabled.",
          "**Performance & analytics** — Help us understand how visitors use the site so we can improve it. May include Google Analytics or similar.",
          "**Functional** — Remember your preferences (theme, form drafts) to improve your experience.",
          "**Marketing (optional)** — Only set when you opt in. Used to measure the effectiveness of our communications.",
        ],
      },
      {
        heading: "3. Similar technologies",
        body: [
          "We may also use pixel tags, local storage, and session storage to deliver and improve the services. These operate similarly to cookies and are covered by this policy.",
        ],
      },
      {
        heading: "4. Third-party cookies",
        body: [
          "Some cookies are placed by third-party services we integrate (analytics, embedded YouTube videos, payment processors). Their cookies are governed by their own privacy policies.",
        ],
      },
      {
        heading: "5. Managing cookies",
        body: [
          "You can control cookies through your browser settings — accept, block, or delete cookies. Blocking strictly-necessary cookies may prevent parts of the site from functioning. For guidance, visit https://www.allaboutcookies.org.",
          "For California residents: see our *Do Not Sell or Share My Personal Information* page.",
        ],
      },
      {
        heading: "6. Changes to this policy",
        body: [
          "We may update this Cookie Policy from time to time to reflect changes in the cookies we use. The \"Effective\" date at the top will indicate the last update.",
        ],
      },
      {
        heading: "7. Contact",
        body: [`${COMPANY} · ${CITY} · Email: ${CONTACT_EMAIL}`],
      },
    ],
  },

  "dmca-policy": {
    title: "DMCA Policy",
    kicker: "How to submit a copyright infringement notice or counter-notice",
    effective: EFFECTIVE,
    sections: [
      {
        heading: "1. Our commitment to copyright",
        body: [
          `${COMPANY} respects the intellectual property rights of others and expects users and clients to do the same. In accordance with the Digital Millennium Copyright Act ("DMCA"), 17 U.S.C. § 512, we will respond to clear notices of alleged copyright infringement.`,
        ],
      },
      {
        heading: "2. Filing a DMCA notice",
        body: [
          "If you believe material posted on or delivered by us infringes your copyright, please send a written notice to our Designated Agent (see Section 5) that includes all of the following:",
          "**(a)** A physical or electronic signature of the copyright owner or an authorized agent.",
          "**(b)** Identification of the copyrighted work you claim has been infringed (or, if multiple works, a representative list).",
          "**(c)** Identification of the material claimed to be infringing, with sufficient detail (URL, timestamp) so we can locate it.",
          "**(d)** Contact information: your name, mailing address, telephone number, and email address.",
          "**(e)** A statement that you have a good-faith belief that the use is not authorized by the copyright owner, its agent, or the law.",
          "**(f)** A statement, made under penalty of perjury, that the information in the notice is accurate and that you are the copyright owner or authorized to act on the owner's behalf.",
        ],
      },
      {
        heading: "3. Counter-notice",
        body: [
          "If you believe material you posted was removed by mistake or misidentification, you may file a counter-notice with our Designated Agent containing:",
          "**(a)** Your physical or electronic signature.",
          "**(b)** Identification of the material removed and its previous location.",
          "**(c)** A statement under penalty of perjury that you have a good-faith belief the material was removed by mistake or misidentification.",
          "**(d)** Your name, address, telephone number, and a statement that you consent to the jurisdiction of the federal court in the district where you reside (or, if outside the US, the Middle District of Florida), and that you will accept service of process from the notifying party.",
        ],
      },
      {
        heading: "4. Repeat infringers",
        body: [
          "We will terminate the accounts or engagements of users or clients who are found to be repeat infringers.",
        ],
      },
      {
        heading: "5. Designated Agent",
        body: [
          `Copyright Agent · ${COMPANY} · ${CITY} · Email: ${CONTACT_EMAIL}`,
          "Please include the subject line: **DMCA Notice**.",
        ],
      },
      {
        heading: "6. Misrepresentation warning",
        body: [
          "Under 17 U.S.C. § 512(f), any person who knowingly and materially misrepresents that material is infringing (or that material was removed by mistake or misidentification) may be liable for damages, including costs and attorneys' fees.",
        ],
      },
    ],
  },
};

export const POLICY_ORDER = [
  "privacy-policy",
  "terms-of-use",
  "do-not-sell",
  "cookie-policy",
  "dmca-policy",
];

export const POLICY_META = {
  "privacy-policy": { label: "Privacy Policy" },
  "terms-of-use": { label: "Terms of Use" },
  "do-not-sell": { label: "Do Not Sell or Share My Personal Information" },
  "cookie-policy": { label: "Cookie Policy" },
  "dmca-policy": { label: "DMCA Policy" },
};
